import React, { useMemo } from 'react';
import { Row, Col, Grid} from "antd";
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import SectorDeviationTable from "./SectorDeviationTable";
import { computeSectorDeviations } from "../utils/smaUtils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const { useBreakpoint } = Grid;


// Helper function to wrap long labels
const formatSectorName = (name) => {
  if (!name) return '';
  // Remove any trailing dots and clean up
  const cleanName = name.replace(/\.+$/, '').replace(/\s+/g, ' ').trim();

  // Handle special cases
  const mapping = {
    'information techno': 'Technology',
    'communication serv': 'Communication',
    'consumer discretio': 'Cyclicals',
    'consumer staples': 'Staples',
    'real estate': 'Real Estate',
    'health care': 'Healthcare'
  };

  // Check for matches in our mapping
  const key = Object.keys(mapping).find(k => cleanName.toLowerCase().includes(k));
  if (key) return mapping[key];

  // Default formatting for other cases
  return cleanName.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Custom tooltip with backdrop blur
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="rounded-lg shadow-lg overflow-hidden" style={{
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(0, 0, 0, 0.1)',
      padding: '8px 12px'
    }}>
      <div className="font-medium mb-1">{formatSectorName(label)}</div>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2 text-sm">
          <div className="w-3 h-3 rounded-full" style={{
            backgroundColor: entry.color || entry.fill
          }} />
          <span>{entry.name}: {Number(entry.value).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
};

const SectorComparisonChart = ({ data }) => {
  const maxValue = Math.ceil(Math.max(
    ...data.map(d => Math.max(d['Full Portfolio'] || 0, d['Reduced Stocks'] || 0))
  ) / 5) * 5;

  // Sort data by Full Portfolio value descending
  const sortedData = [...data].sort((a, b) => b['Full Portfolio'] - a['Full Portfolio']);

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={sortedData}
          layout="vertical"
          margin={{
            top: 5,
            right: 20,
            left: 20,
            bottom: 5
          }}
          barGap={4}
        >
          {/* Existing chart configuration */}
          <defs>
            <linearGradient id="fullPortfolioGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#000000" stopOpacity={0.85} />
              <stop offset="100%" stopColor="#000000" stopOpacity={0.95} />
            </linearGradient>
            <linearGradient id="reducedStocksGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#0083ff" stopOpacity={0.85} />
              <stop offset="100%" stopColor="#0083ff" stopOpacity={0.95} />
            </linearGradient>
            <filter id="barShadow" height="130%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="1"/>
              <feOffset dx="1" dy="1" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.2"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          <CartesianGrid
            strokeDasharray="2 2"
            horizontal={false}
            stroke="rgba(0,0,0,0.1)"
          />

          <XAxis
            type="number"
            domain={[0, maxValue]}
            tickFormatter={(value) => `${Math.round(value)}%`}
            ticks={Array.from({length: maxValue/5 + 1}, (_, i) => i * 5)}
            tick={{
              fill: 'var(--font-main)',
              fontSize: 12
            }}
            axisLine={{ stroke: '#eeefff' }}
          />

          <YAxis
            type="category"
            dataKey="id"
            tickFormatter={formatSectorName}
            interval={0}
            axisLine={{ stroke: '#eeefff' }}
            tick={{
              fill: 'var(--font-main)',
              fontSize: 12
            }}
            width={140}
          />

          <Tooltip
            content={<CustomTooltip />}
            cursor={{
              fill: 'rgba(0, 0, 0, 0.05)'
            }}
          />

          <Legend
            wrapperStyle={{
              paddingTop: '12px'
            }}
          />

          <Bar
            dataKey="Full Portfolio"
            name="Full Portfolio"
            fill="url(#fullPortfolioGradient)"
            filter="url(#barShadow)"
          />

          <Bar
            dataKey="Reduced Stocks"
            name="Reduced Stocks"
            fill="url(#reducedStocksGradient)"
            filter="url(#barShadow)"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};


const SectorComparisonBarChart = ({ sectorsBarChartField, setSectorsBarChartField, sectorsBarData, summaryDetails}) => {

    const bp = useBreakpoint();
    const sectorDeviations = useMemo(() => {
        return computeSectorDeviations(summaryDetails.sectorDistribution, summaryDetails.originalSectorDistribution)
    }, [summaryDetails.sectorDistribution, summaryDetails.originalSectorDistribution]);

    const ToggleButtons = ()=>(
        <ButtonGroup>
            <Button size={'small'} variant={sectorsBarChartField === 'wgt' ? 'contained' : 'text'} onClick={() => setSectorsBarChartField('wgt')}>Weight</Button>
            <Button size={'small'} variant={sectorsBarChartField === 'count' ? 'contained' : 'text'} onClick={() => setSectorsBarChartField('count')}>Count</Button>
        </ButtonGroup>
    )

    return (
        <div className={'h-full overflow-y-auto overflow-x-hidden px-6 2xl:pt-10'}>
            {bp.xxl && (
                <Row gutter={64} align={'top'} justify={'center'}>
                    <Col span={12}>
                        <div style={{width: '100%', height: '75vh'}}>
                            <SectorComparisonChart data={sectorsBarData} />
                        </div>
                    </Col>
                    <Col span={12}>
                        <ToggleButtons />
                        <SectorDeviationTable deviations={sectorDeviations} />
                    </Col>
                </Row>
            )}
            {!bp.xxl&&(
                <div className={'flex flex-col !overflow-y-scroll'}>
                    <div style={{width: '100%', height: '75vh'}}>
                        <SectorComparisonChart data={sectorsBarData}/>
                    </div>
                    <div className={'px-8'}>
                        <SectorDeviationTable deviations={sectorDeviations} />
                    </div>
                </div>
            )}
        </div>

    );
}

export default SectorComparisonBarChart;
